<template>
  <div class="md-layout shipping-page">
    <div class="md-layout-item">
      <md-card class="main-card">
        <md-card-header class="card-header">
          <div class="header-left">
            <div class="card-icon">
              <md-icon>local_shipping</md-icon>
            </div>
            <div>
              <h4 class="title">Shipping Methods</h4>
              <span class="subtitle">
                {{ methods.length }} method{{ methods.length !== 1 ? "s" : "" }}
                configured
              </span>
            </div>
          </div>
          <md-button class="md-raised add-btn" @click="openCreateMethod">
            <md-icon>add</md-icon> Add Method
          </md-button>
        </md-card-header>

        <md-card-content class="table-content">
          <div v-if="loading" class="state-box">
            <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="36"
              :md-stroke="3"
            />
            <p>Loading...</p>
          </div>

          <div v-else-if="methods.length === 0" class="state-box">
            <md-icon class="empty-icon">local_shipping</md-icon>
            <p>No shipping methods yet</p>
            <small>Add your first shipping method to get started.</small>
          </div>

          <div v-else>
            <div
              v-for="method in methods"
              :key="method._id"
              class="method-card"
              :class="{ inactive: !method.isActive }"
            >
              <div class="method-header">
                <div class="method-info">
                  <div class="method-icon-wrap">
                    <md-icon>{{ method.icon || "local_shipping" }}</md-icon>
                  </div>
                  <div>
                    <strong class="method-name">{{ method.name }}</strong>
                    <div class="method-meta">
                      <span class="type-badge" :class="'type-' + method.type">{{
                        method.type
                      }}</span>
                      <span class="text-muted small">{{
                        method.description || "No description"
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="method-actions">
                  <span
                    class="status-badge"
                    :class="method.isActive ? 'active' : 'inactive'"
                  >
                    {{ method.isActive ? "Active" : "Inactive" }}
                  </span>
                  <md-button
                    class="md-just-icon md-simple action-btn btn-info"
                    title="Add Market Rate"
                    @click="openAddRate(method)"
                  >
                    <md-icon>add_location</md-icon>
                  </md-button>
                  <md-button
                    class="md-just-icon md-simple action-btn btn-warning"
                    title="Edit"
                    @click="openEditMethod(method)"
                  >
                    <md-icon>edit</md-icon>
                  </md-button>
                  <md-button
                    class="md-just-icon md-simple action-btn"
                    :class="method.isActive ? 'btn-muted' : 'btn-success'"
                    @click="toggleMethod(method)"
                  >
                    <md-icon>{{
                      method.isActive ? "toggle_on" : "toggle_off"
                    }}</md-icon>
                  </md-button>
                  <md-button
                    class="md-just-icon md-simple action-btn btn-danger"
                    title="Delete"
                    @click="deleteMethod(method)"
                  >
                    <md-icon>delete_outline</md-icon>
                  </md-button>
                </div>
              </div>

              <div
                class="rates-section"
                v-if="method.marketRates && method.marketRates.length > 0"
              >
                <table class="rates-table">
                  <thead>
                    <tr>
                      <th>Market</th>
                      <th>Price</th>
                      <th>Free Above</th>
                      <th>Min Cart</th>
                      <th>Max Cart</th>
                      <th>Est. Days</th>
                      <th>Cutoff</th>
                      <th>Products</th>
                      <th>Stripe Rate</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="rate in method.marketRates"
                      :key="rate._id"
                      class="rate-row"
                      :class="{ 'rate-inactive': !rate.isActive }"
                    >
                      <td>
                        <div class="market-cell">
                          <span
                            class="fi"
                            :class="'fi-' + rate.marketplaceCode.toLowerCase()"
                          ></span>
                          <span class="market-code">{{
                            rate.marketplaceCode
                          }}</span>
                        </div>
                      </td>
                      <td>
                        <strong
                          >{{ getCurrencySymbol(rate.currency)
                          }}{{ rate.price }}</strong
                        >
                        <small v-if="rate.price === 0" class="free-tag"
                          >FREE</small
                        >
                      </td>
                      <td>
                        <span
                          v-if="
                            rate.freeAboveAmount !== null &&
                            rate.freeAboveAmount !== undefined
                          "
                        >
                          {{ getCurrencySymbol(rate.currency)
                          }}{{ rate.freeAboveAmount }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="rate.minCartValue">
                          {{ getCurrencySymbol(rate.currency)
                          }}{{ rate.minCartValue }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td>
                        <span v-if="rate.maxCartValue">
                          {{ getCurrencySymbol(rate.currency)
                          }}{{ rate.maxCartValue }}
                        </span>
                        <span v-else class="text-muted">-</span>
                      </td>
                      <td class="text-muted small">
                        {{ rate.estimatedDays || "-" }}
                      </td>
                      <td>
                        <span v-if="rate.cutoffTime" class="cutoff-tag">{{
                          rate.cutoffTime
                        }}</span>
                        <span v-if="rate.weekdaysOnly" class="weekday-tag"
                          >Weekdays</span
                        >
                        <span
                          v-if="!rate.cutoffTime && !rate.weekdaysOnly"
                          class="text-muted"
                          >-</span
                        >
                      </td>
                      <td>
                        <span
                          v-if="
                            !rate.restrictToProductIds ||
                            rate.restrictToProductIds.length === 0
                          "
                          class="text-muted small"
                        >
                          All
                        </span>
                        <div v-else class="product-chips-sm">
                          <span
                            v-for="id in rate.restrictToProductIds"
                            :key="id"
                            class="product-chip-sm"
                            :title="getProductName(id)"
                          >
                            {{ getProductName(id) }}
                          </span>
                        </div>
                      </td>
                      <td>
                        <div v-if="rate.stripeShippingRateId">
                          <code class="stripe-id">{{
                            rate.stripeShippingRateId
                          }}</code>
                        </div>
                        <button
                          v-else
                          class="sync-btn"
                          title="Create Stripe rate"
                          @click="syncStripeRate(method, rate)"
                        >
                          <md-icon>sync</md-icon> Sync
                        </button>
                      </td>
                      <td>
                        <span
                          class="status-badge"
                          :class="rate.isActive ? 'active' : 'inactive'"
                        >
                          {{ rate.isActive ? "Active" : "Inactive" }}
                        </span>
                      </td>
                      <td>
                        <div class="actions-row">
                          <md-button
                            class="
                              md-just-icon md-simple
                              action-btn
                              btn-warning
                            "
                            title="Edit Rate"
                            @click="openEditRate(method, rate)"
                          >
                            <md-icon>edit</md-icon>
                          </md-button>
                          <md-button
                            class="md-just-icon md-simple action-btn"
                            :class="rate.isActive ? 'btn-muted' : 'btn-success'"
                            @click="toggleRate(method, rate)"
                          >
                            <md-icon>{{
                              rate.isActive ? "toggle_on" : "toggle_off"
                            }}</md-icon>
                          </md-button>
                          <md-button
                            class="md-just-icon md-simple action-btn btn-danger"
                            title="Delete Rate"
                            @click="deleteRate(method, rate)"
                          >
                            <md-icon>delete_outline</md-icon>
                          </md-button>
                        </div>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div v-else class="no-rates">
                <md-icon>info_outline</md-icon>
                No market rates configured yet.
                <button class="add-rate-link" @click="openAddRate(method)">
                  Add first rate
                </button>
              </div>
            </div>
          </div>
        </md-card-content>
      </md-card>
    </div>

    <!-- Method Create/Edit Modal -->
    <transition name="modal-fade">
      <div
        v-if="showMethodModal"
        class="modal-overlay"
        @click.self="closeMethodModal"
      >
        <div class="modal-box">
          <div class="modal-header">
            <h5>
              {{
                methodEditMode ? "Edit Shipping Method" : "Add Shipping Method"
              }}
            </h5>
            <button class="modal-close" @click="closeMethodModal">
              <md-icon>close</md-icon>
            </button>
          </div>
          <div class="modal-body">
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Name <span class="req">*</span></label
                >
                <input
                  v-model="methodForm.name"
                  class="field-input"
                  placeholder="e.g. Standard Delivery"
                />
              </div>
              <div class="form-group">
                <label class="field-label"
                  >Type <span class="req">*</span></label
                >
                <select
                  v-model="methodForm.type"
                  class="field-input field-select"
                  :disabled="methodEditMode"
                >
                  <option value="">- select -</option>
                  <option value="standard">Standard</option>
                  <option value="next_day">Next Day</option>
                  <option value="express">Express</option>
                  <option value="free">Free</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="field-label">Description</label>
              <input
                v-model="methodForm.description"
                class="field-input"
                placeholder="e.g. Delivered in 3-5 business days"
              />
            </div>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Icon (Material Icon name)</label>
                <input
                  v-model="methodForm.icon"
                  class="field-input"
                  placeholder="local_shipping"
                />
              </div>
              <div class="form-group">
                <label class="field-label">Sort Order</label>
                <input
                  v-model.number="methodForm.sortOrder"
                  type="number"
                  min="0"
                  class="field-input"
                  placeholder="0"
                />
              </div>
            </div>
            <div class="form-group toggle-group">
              <label class="field-label">Active</label>
              <div class="toggle-row">
                <md-switch v-model="methodForm.isActive" />
                <span class="toggle-text">{{
                  methodForm.isActive ? "Active" : "Inactive"
                }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeMethodModal">Cancel</button>
            <button
              class="btn-save"
              :disabled="savingMethod"
              @click="saveMethod"
            >
              <i v-if="savingMethod" class="fa fa-spinner fa-spin"></i>
              <md-icon v-else>check</md-icon>
              {{ methodEditMode ? "Save Changes" : "Create Method" }}
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Market Rate Add/Edit Modal -->
    <transition name="modal-fade">
      <div
        v-if="showRateModal"
        class="modal-overlay"
        @click.self="closeRateModal"
      >
        <div class="modal-box modal-box-wide">
          <div class="modal-header">
            <h5>
              {{ rateEditMode ? "Edit Market Rate" : "Add Market Rate" }} —
              {{ activeMethod && activeMethod.name }}
            </h5>
            <button class="modal-close" @click="closeRateModal">
              <md-icon>close</md-icon>
            </button>
          </div>
          <div class="modal-body">
            <div v-if="!rateEditMode" class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Marketplace <span class="req">*</span></label
                >
                <select
                  v-model="rateForm.marketplaceId"
                  class="field-input field-select"
                  @change="onMarketplaceSelect"
                >
                  <option value="">- select marketplace -</option>
                  <option
                    v-for="m in availableMarketplaces"
                    :key="m._id"
                    :value="m._id"
                  >
                    {{ m.code }} - {{ m.country }}
                  </option>
                </select>
              </div>
              <div class="form-group">
                <label class="field-label">Market Code</label>
                <input
                  :value="rateForm.marketplaceCode || '-'"
                  class="field-input"
                  disabled
                />
              </div>
            </div>

            <div class="section-divider"><span>Pricing</span></div>
            <div class="form-row three-col">
              <div class="form-group">
                <label class="field-label"
                  >Shipping Price <span class="req">*</span></label
                >
                <div class="input-prefix-wrap">
                  <span class="input-prefix">{{
                    rateForm.currencySymbol || "£"
                  }}</span>
                  <input
                    v-model.number="rateForm.price"
                    type="number"
                    min="0"
                    step="0.01"
                    class="field-input prefix-input"
                    placeholder="0.00"
                  />
                </div>
                <small class="hint">Set 0 for always free</small>
              </div>
              <div class="form-group">
                <label class="field-label">Free Above Cart Value</label>
                <div class="input-prefix-wrap">
                  <span class="input-prefix">{{
                    rateForm.currencySymbol || "£"
                  }}</span>
                  <input
                    v-model.number="rateForm.freeAboveAmount"
                    type="number"
                    min="0"
                    step="0.01"
                    class="field-input prefix-input"
                    placeholder="e.g. 50.00"
                  />
                </div>
                <small class="hint">Leave empty for no free threshold</small>
              </div>
              <div class="form-group">
                <label class="field-label">Currency</label>
                <input
                  :value="rateForm.currency || '-'"
                  class="field-input"
                  disabled
                />
              </div>
            </div>

            <div class="section-divider"><span>Cart Restrictions</span></div>
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Min Cart Value</label>
                <div class="input-prefix-wrap">
                  <span class="input-prefix">{{
                    rateForm.currencySymbol || "£"
                  }}</span>
                  <input
                    v-model.number="rateForm.minCartValue"
                    type="number"
                    min="0"
                    step="0.01"
                    class="field-input prefix-input"
                    placeholder="e.g. 10.00"
                  />
                </div>
                <small class="hint">Leave empty for no minimum.</small>
              </div>
              <div class="form-group">
                <label class="field-label">Max Cart Value</label>
                <div class="input-prefix-wrap">
                  <span class="input-prefix">{{
                    rateForm.currencySymbol || "£"
                  }}</span>
                  <input
                    v-model.number="rateForm.maxCartValue"
                    type="number"
                    min="0"
                    step="0.01"
                    class="field-input prefix-input"
                    placeholder="e.g. 500.00"
                  />
                </div>
                <small class="hint">Leave empty for no limit.</small>
              </div>
            </div>

            <div class="section-divider"><span>Delivery Info</span></div>
            <div class="form-row three-col">
              <div class="form-group">
                <label class="field-label">Estimated Days</label>
                <input
                  v-model="rateForm.estimatedDays"
                  class="field-input"
                  placeholder="e.g. 3-5 days"
                />
              </div>
              <div
                v-if="activeMethod && activeMethod.type === 'next_day'"
                class="form-group"
              >
                <label class="field-label">Order Cutoff Time</label>
                <input
                  v-model="rateForm.cutoffTime"
                  class="field-input"
                  placeholder="e.g. 14:00"
                />
                <small class="hint"
                  >Orders before this time qualify for next day</small
                >
              </div>
              <div
                v-if="activeMethod && activeMethod.type === 'next_day'"
                class="form-group"
              >
                <label class="field-label">Weekdays Only</label>
                <div class="toggle-row" style="margin-top: 8px">
                  <md-switch v-model="rateForm.weekdaysOnly" />
                  <span class="toggle-text">
                    {{ rateForm.weekdaysOnly ? "Yes - Mon to Fri only" : "No" }}
                  </span>
                </div>
              </div>
            </div>

            <div class="section-divider"><span>Product Restriction</span></div>
            <div class="form-group">
              <label class="restriction-label">
                <md-icon
                  style="
                    font-size: 16px;
                    vertical-align: middle;
                    color: #0277bd;
                  "
                  >inventory_2</md-icon
                >
                Eligible Products
                <small class="restriction-hint">
                  Leave empty = available for ALL products. If set, ALL cart
                  items must be eligible.
                </small>
              </label>
              <div
                v-if="
                  rateForm.restrictToProductIds &&
                  rateForm.restrictToProductIds.length > 0
                "
                class="product-chips"
              >
                <span
                  v-for="id in rateForm.restrictToProductIds"
                  :key="id"
                  class="product-chip"
                >
                  {{ getProductName(id) }}
                  <span class="chip-remove" @click="removeRestrictedProduct(id)"
                    >×</span
                  >
                </span>
              </div>
              <div v-else class="restriction-empty">
                <md-icon style="font-size: 14px; color: #aaa; margin-right: 4px"
                  >info_outline</md-icon
                >
                No restriction — available for all products
              </div>
              <select
                v-model="restrictProductSelect"
                class="field-input field-select"
                style="margin-top: 8px"
                @change="onRestrictProductSelected"
              >
                <option value="">+ Add product restriction...</option>
                <option
                  v-for="product in onetimeProducts"
                  :key="product._id"
                  :value="product._id"
                  :disabled="
                    rateForm.restrictToProductIds &&
                    rateForm.restrictToProductIds.includes(product._id)
                  "
                >
                  {{ product.displayName || product.name }}
                </option>
              </select>
            </div>

            <div class="section-divider"><span>Status</span></div>
            <div class="form-group toggle-group">
              <label class="field-label">Active</label>
              <div class="toggle-row">
                <md-switch v-model="rateForm.isActive" />
                <span class="toggle-text">{{
                  rateForm.isActive ? "Active" : "Inactive"
                }}</span>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button class="btn-cancel" @click="closeRateModal">Cancel</button>
            <button class="btn-save" :disabled="savingRate" @click="saveRate">
              <i v-if="savingRate" class="fa fa-spinner fa-spin"></i>
              <md-icon v-else>check</md-icon>
              {{ rateEditMode ? "Save Changes" : "Add Rate" }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ShippingService from "../../services/SuperAdmin/Kids/ShippingService";
import ProductService from "../../services/SuperAdmin/Kids/ProductService";
import OrdersDataService from "../../services/SuperAdmin/Kids/OrdersDataService";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      loading: false,
      methods: [],
      marketplaces: [],
      onetimeProducts: [],
      restrictProductSelect: "",

      showMethodModal: false,
      methodEditMode: false,
      savingMethod: false,
      methodForm: this.defaultMethodForm(),

      showRateModal: false,
      rateEditMode: false,
      savingRate: false,
      activeMethod: null,
      activeRate: null,
      rateForm: this.defaultRateForm(),
    };
  },
  computed: {
    availableMarketplaces() {
      if (!this.activeMethod) return this.marketplaces;
      const existingCodes = (this.activeMethod.marketRates || []).map(
        (r) => r.marketplaceCode
      );
      return this.marketplaces.filter((m) => !existingCodes.includes(m.code));
    },
  },
  beforeMount() {
    this.loadData();
  },
  mounted() {
    this.loadOnetimeProducts();
  },
  methods: {
    defaultMethodForm() {
      return {
        name: "",
        type: "",
        description: "",
        icon: "local_shipping",
        sortOrder: 0,
        isActive: true,
      };
    },
    defaultRateForm() {
      return {
        marketplaceId: "",
        marketplaceCode: "",
        currency: "",
        currencySymbol: "",
        price: null,
        freeAboveAmount: null,
        minCartValue: null,
        maxCartValue: null,
        estimatedDays: "",
        cutoffTime: "",
        weekdaysOnly: false,
        isActive: true,
        restrictToProductIds: [],
      };
    },
    getCurrencySymbol(currency) {
      const map = {
        GBP: "£",
        EUR: "€",
        USD: "$",
        INR: "₹",
        SEK: "kr",
        DKK: "kr",
        PLN: "zł",
      };
      return map[(currency || "").toUpperCase()] || currency;
    },

    // Product restriction
    getProductName(productId) {
      const p = this.onetimeProducts.find(
        (p) => p._id === productId || p._id.toString() === productId.toString()
      );
      return p ? p.displayName || p.name : productId;
    },
    onRestrictProductSelected() {
      const productId = this.restrictProductSelect;
      if (!productId) return;
      if (!this.rateForm.restrictToProductIds)
        this.rateForm.restrictToProductIds = [];
      if (!this.rateForm.restrictToProductIds.includes(productId)) {
        this.rateForm.restrictToProductIds.push(productId);
      }
      this.$nextTick(() => {
        this.restrictProductSelect = "";
      });
    },
    removeRestrictedProduct(productId) {
      this.rateForm.restrictToProductIds =
        this.rateForm.restrictToProductIds.filter((id) => id !== productId);
    },
    async loadOnetimeProducts() {
      try {
        const res = await OrdersDataService.wingioProductsList();
        this.onetimeProducts = (res.data.data || []).filter(
          (p) => p.productType === "Onetime"
        );
      } catch (e) {
        
      }
    },

    // Method modal
    openCreateMethod() {
      this.methodForm = this.defaultMethodForm();
      this.methodEditMode = false;
      this.showMethodModal = true;
    },
    openEditMethod(method) {
      this.methodForm = {
        _id: method._id,
        name: method.name,
        type: method.type,
        description: method.description || "",
        icon: method.icon || "local_shipping",
        sortOrder: method.sortOrder || 0,
        isActive: method.isActive,
      };
      this.methodEditMode = true;
      this.showMethodModal = true;
    },
    closeMethodModal() {
      this.showMethodModal = false;
    },
    async saveMethod() {
      if (!this.methodForm.name || !this.methodForm.type) {
        this.$toast.error("Name and Type are required.");
        return;
      }
      this.savingMethod = true;
      try {
        if (this.methodEditMode) {
          await ShippingService.updateMethod(this.methodForm);
          this.$toast.success("Shipping method updated.");
        } else {
          await ShippingService.createMethod(this.methodForm);
          this.$toast.success("Shipping method created.");
        }
        this.closeMethodModal();
        this.loadData();
      } catch (e) {
        this.$toast.error(
          (e.response && e.response.data && e.response.data.respones_msg) ||
            "Failed."
        );
      } finally {
        this.savingMethod = false;
      }
    },
    async toggleMethod(method) {
      try {
        await ShippingService.toggleMethodActive({
          _id: method._id,
          isActive: !method.isActive,
        });
        this.$toast.success(
          `Method ${method.isActive ? "deactivated" : "activated"}.`
        );
        this.loadData();
      } catch (e) {
        this.$toast.error(
          (e.response && e.response.data && e.response.data.respones_msg) ||
            "Failed."
        );
      }
    },
    deleteMethod(method) {
      Swal.fire({
        title: "Delete method?",
        text: `"${method.name}" and all its market rates will be removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ShippingService.deleteMethod({ id: method._id });
            this.$toast.success("Shipping method deleted.");
            this.loadData();
          } catch (e) {
            this.$toast.error(
              (e.response && e.response.data && e.response.data.respones_msg) ||
                "Failed."
            );
          }
        }
      });
    },

    // Rate modal
    openAddRate(method) {
      this.activeMethod = method;
      this.rateForm = this.defaultRateForm();
      this.restrictProductSelect = "";
      this.rateEditMode = false;
      this.showRateModal = true;
    },
    openEditRate(method, rate) {
      this.activeMethod = method;
      this.activeRate = rate;
      const marketplace = this.marketplaces.find(
        (m) => m.code === rate.marketplaceCode
      );
      this.rateForm = {
        marketplaceId: rate.marketplaceId || "",
        marketplaceCode: rate.marketplaceCode,
        currency: rate.currency,
        currencySymbol:
          marketplace && marketplace.currencyId
            ? marketplace.currencyId.symbol
            : this.getCurrencySymbol(rate.currency),
        price: rate.price,
        freeAboveAmount:
          rate.freeAboveAmount !== undefined ? rate.freeAboveAmount : null,
        minCartValue: rate.minCartValue || null,
        maxCartValue: rate.maxCartValue || null,
        estimatedDays: rate.estimatedDays || "",
        cutoffTime: rate.cutoffTime || "",
        weekdaysOnly: rate.weekdaysOnly || false,
        isActive: rate.isActive,
        _rateId: rate._id,
        restrictToProductIds: rate.restrictToProductIds
          ? rate.restrictToProductIds.map((id) => id.toString())
          : [],
      };
      this.restrictProductSelect = "";
      this.rateEditMode = true;
      this.showRateModal = true;
    },
    closeRateModal() {
      this.showRateModal = false;
      this.activeMethod = null;
      this.activeRate = null;
      this.restrictProductSelect = "";
    },
    onMarketplaceSelect() {
      const m = this.marketplaces.find(
        (x) => x._id === this.rateForm.marketplaceId
      );
      if (m) {
        this.rateForm.marketplaceCode = m.code;
        this.rateForm.currency = m.currencyId ? m.currencyId.code : "";
        this.rateForm.currencySymbol = m.currencyId ? m.currencyId.symbol : "";
      }
    },
    async saveRate() {
      if (!this.rateEditMode && !this.rateForm.marketplaceId) {
        this.$toast.error("Please select a marketplace.");
        return;
      }
      if (this.rateForm.price === null || this.rateForm.price === "") {
        this.$toast.error("Price is required.");
        return;
      }
      this.savingRate = true;
      try {
        const payload = {
          price: this.rateForm.price,
          freeAboveAmount: this.rateForm.freeAboveAmount,
          minCartValue: this.rateForm.minCartValue,
          maxCartValue: this.rateForm.maxCartValue,
          estimatedDays: this.rateForm.estimatedDays,
          cutoffTime: this.rateForm.cutoffTime,
          weekdaysOnly: this.rateForm.weekdaysOnly,
          isActive: this.rateForm.isActive,
          restrictToProductIds: this.rateForm.restrictToProductIds || [],
        };
        if (this.rateEditMode) {
          await ShippingService.updateRate({
            shippingMasterId: this.activeMethod._id,
            rateId: this.rateForm._rateId,
            ...payload,
          });
          this.$toast.success("Market rate updated.");
        } else {
          await ShippingService.addRate({
            shippingMasterId: this.activeMethod._id,
            marketplaceId: this.rateForm.marketplaceId,
            marketplaceCode: this.rateForm.marketplaceCode,
            currency: this.rateForm.currency,
            ...payload,
          });
          this.$toast.success("Market rate added.");
        }
        this.closeRateModal();
        this.loadData();
      } catch (e) {
        this.$toast.error(
          (e.response && e.response.data && e.response.data.respones_msg) ||
            "Failed."
        );
      } finally {
        this.savingRate = false;
      }
    },
    async toggleRate(method, rate) {
      try {
        await ShippingService.toggleRate({
          shippingMasterId: method._id,
          rateId: rate._id,
          isActive: !rate.isActive,
        });
        this.$toast.success(
          `Rate ${rate.isActive ? "deactivated" : "activated"}.`
        );
        this.loadData();
      } catch (e) {
        this.$toast.error(
          (e.response && e.response.data && e.response.data.respones_msg) ||
            "Failed."
        );
      }
    },
    deleteRate(method, rate) {
      Swal.fire({
        title: "Delete rate?",
        text: `${rate.marketplaceCode} rate will be removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ShippingService.deleteRate({
              shippingMasterId: method._id,
              rateId: rate._id,
            });
            this.$toast.success("Rate deleted.");
            this.loadData();
          } catch (e) {
            this.$toast.error(
              (e.response && e.response.data && e.response.data.respones_msg) ||
                "Failed."
            );
          }
        }
      });
    },
    async syncStripeRate(method, rate) {
      try {
        await ShippingService.syncStripeRate({
          shippingMasterId: method._id,
          rateId: rate._id,
        });
        this.$toast.success("Stripe rate synced.");
        this.loadData();
      } catch (e) {
        this.$toast.error(
          (e.response && e.response.data && e.response.data.respones_msg) ||
            "Sync failed."
        );
      }
    },
    loadData() {
      this.loading = true;
      Promise.all([
        ShippingService.listMethods(),
        ProductService.marketplaceList(),
      ])
        .then(([methodsRes, mpRes]) => {
          this.methods = methodsRes.data.data;
          this.marketplaces = mpRes.data.data.filter((m) => m.isActive);
        })
        .catch((e) => {
          this.$toast.error(
            (e.response && e.response.data && e.response.data.respones_msg) ||
              "Failed to load."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.shipping-page {
  font-family: "Segoe UI", sans-serif;
}

.main-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, #0277bd 0%, #01579b 100%);
  color: white;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.card-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.card-icon .md-icon {
  color: white !important;
}

.title {
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}

.subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}

.add-btn {
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.table-content {
  padding: 16px;
}

.method-card {
  border: 1.5px solid #e0e0e0;
  border-radius: 12px;
  margin-bottom: 16px;
  overflow: hidden;
}

.method-card.inactive {
  opacity: 0.65;
}

.method-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 18px;
  background: #f8f9fa;
  border-bottom: 1px solid #e0e0e0;
}

.method-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.method-icon-wrap {
  width: 40px;
  height: 40px;
  background: #e3f2fd;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.method-icon-wrap .md-icon {
  color: #0277bd !important;
}

.method-name {
  font-size: 15px;
  font-weight: 700;
  color: #333;
}

.method-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 3px;
}

.method-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.type-badge {
  padding: 2px 10px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
}

.type-standard {
  background: #e3f2fd;
  color: #0277bd;
}

.type-next_day {
  background: #fff3e0;
  color: #e65100;
}

.type-express {
  background: #fce4ec;
  color: #c62828;
}

.type-free {
  background: #e8f5e9;
  color: #2e7d32;
}

.rates-section {
  padding: 0;
}

.rates-table {
  width: 100%;
  border-collapse: collapse;
}

.rates-table th {
  padding: 10px 14px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #666;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
}

.rates-table td {
  padding: 10px 14px;
  font-size: 13px;
  border-bottom: 1px solid #f5f5f5;
  vertical-align: middle;
}

.rate-row:hover td {
  background: #f5f9ff;
}

.rate-inactive td {
  background: #fafafa;
  color: #bbb;
}

.no-rates {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 14px 18px;
  font-size: 13px;
  color: #aaa;
}

.no-rates .md-icon {
  color: #ccc !important;
  font-size: 18px !important;
}

.add-rate-link {
  background: none;
  border: none;
  color: #0277bd;
  font-size: 13px;
  cursor: pointer;
  font-weight: 600;
  text-decoration: underline;
}

.market-cell {
  display: flex;
  align-items: center;
  gap: 6px;
}

.fi {
  width: 20px;
  height: 15px;
  border-radius: 2px;
}

.market-code {
  font-weight: 700;
  font-size: 12px;
  color: #333;
}

.free-tag {
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 6px;
  border-radius: 6px;
  font-size: 10px;
  font-weight: 700;
  margin-left: 4px;
}

.cutoff-tag {
  background: #fff3e0;
  color: #e65100;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 4px;
}

.weekday-tag {
  background: #f3e5f5;
  color: #6a1b9a;
  padding: 2px 6px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 600;
}

.stripe-id {
  font-family: monospace;
  font-size: 10px;
  color: #888;
  background: #f5f5f5;
  padding: 2px 5px;
  border-radius: 3px;
}

.sync-btn {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  background: none;
  border: 1px dashed #0277bd;
  color: #0277bd;
  border-radius: 6px;
  padding: 2px 8px;
  font-size: 11px;
  cursor: pointer;
  font-weight: 600;
}

.sync-btn .md-icon {
  font-size: 14px !important;
  color: #0277bd !important;
}

.actions-row {
  display: flex;
  gap: 2px;
}

.action-btn.md-just-icon {
  width: 30px;
  height: 30px;
  min-width: 30px;
  border-radius: 7px !important;
}

.action-btn.md-just-icon .md-icon {
  font-size: 16px !important;
}

.action-btn.btn-info .md-icon {
  color: #0277bd !important;
}

.action-btn.btn-warning .md-icon {
  color: #f57c00 !important;
}

.action-btn.btn-danger .md-icon {
  color: #c62828 !important;
}

.action-btn.btn-success .md-icon {
  color: #2e7d32 !important;
}

.action-btn.btn-muted .md-icon {
  color: #888 !important;
}

.status-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}

.status-badge.active {
  background: #e8f5e9;
  color: #2e7d32;
}

.status-badge.inactive {
  background: #fce4ec;
  color: #c62828;
}

.text-muted {
  color: #aaa;
}

.small {
  font-size: 12px;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #aaa;
  text-align: center;
}

.empty-icon {
  font-size: 48px !important;
  color: #ddd !important;
  margin-bottom: 12px;
}

.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-box {
  background: white;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-box-wide {
  max-width: 700px;
}

.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px;
  background: linear-gradient(135deg, #0277bd, #01579b);
  color: white;
  position: sticky;
  top: 0;
  z-index: 1;
}

.modal-header h5 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: white;
}

.modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.modal-close .md-icon {
  color: white !important;
  font-size: 16px !important;
}

.modal-body {
  padding: 20px 24px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  position: sticky;
  bottom: 0;
  background: white;
}

.form-row {
  display: flex;
  gap: 14px;
}

.form-row.two-col > * {
  flex: 1;
}

.form-row.three-col > * {
  flex: 1;
}

.form-group {
  margin-bottom: 14px;
}

.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.req {
  color: #e53935;
}

.hint {
  font-size: 11px;
  color: #aaa;
  display: block;
  margin-top: 3px;
}

.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}

.field-input:focus {
  border-color: #0277bd;
}

.field-input:disabled {
  background: #f5f5f5;
  color: #888;
}

.field-select {
  -webkit-appearance: none;
  cursor: pointer;
}

.input-prefix-wrap {
  position: relative;
}

.input-prefix {
  position: absolute;
  left: 9px;
  top: 50%;
  transform: translateY(-50%);
  color: #888;
  font-size: 14px;
  pointer-events: none;
}

.prefix-input {
  padding-left: 24px;
}

.section-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 16px 0 12px;
}

.section-divider span {
  font-size: 11px;
  font-weight: 700;
  color: #aaa;
  text-transform: uppercase;
  white-space: nowrap;
}

.section-divider::before,
.section-divider::after {
  content: "";
  flex: 1;
  height: 1px;
  background: #e0e0e0;
}

.toggle-group {
  display: flex;
  flex-direction: column;
}

.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.toggle-text {
  font-size: 13px;
  color: #555;
}

.btn-cancel {
  padding: 9px 20px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: white;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: #0277bd;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}

.btn-save:hover:not(:disabled) {
  background: #01579b;
}

.btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-save .md-icon {
  color: white !important;
  font-size: 17px !important;
}

.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}

.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}

.restriction-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.restriction-hint {
  font-weight: 400;
  font-size: 11px;
  color: #999;
  text-transform: none;
  letter-spacing: 0;
}

.restriction-empty {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #aaa;
  padding: 6px 0;
}

.product-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
}

.product-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #e3f2fd;
  color: #0d47a1;
  border: 1px solid #90caf9;
  border-radius: 12px;
  font-size: 12px;
  font-weight: 500;
}

.chip-remove {
  cursor: pointer;
  font-size: 15px;
  line-height: 1;
  color: #0d47a1;
  margin-left: 2px;
}

.chip-remove:hover {
  color: #b71c1c;
}

.product-chips-sm {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.product-chip-sm {
  display: inline-block;
  padding: 2px 7px;
  background: #e8f5e9;
  color: #2e7d32;
  border: 1px solid #a5d6a7;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
}
</style>
